import React, { useState, useEffect, useCallback } from "react";
import { MSG } from "../constants/toastMessages";
import AdminHeader from '../layout/AdminHeader';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { apiCallingTriggerPost } from "../api/services";
import noData from "../assets/images/no-data.png";
import Loader from "../assets/images/loader.gif";
import '../assets/css/admin-toolbar.css';
import Pagination from "react-js-pagination";
import { toast } from 'react-toastify';

function Reprocess() {
  const { isAuthenticated } = useSelector((state) => state.authentication);
  const { userData } = useSelector((state) => state.masterData);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('processing');

  // --- Processing Queue tab state ---
  const [videoList, setVideoList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [itemsCountPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const [olderThanHours, setOlderThanHours] = useState(1);
  const [appliedHours, setAppliedHours] = useState(1);

  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmMode, setConfirmMode] = useState(null); // 'selected' | 'all'
  const [inFlightIds, setInFlightIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('reprocess_inflight') || '[]'); }
    catch { return []; }
  });

  // --- Missing Thumbnails tab state ---
  const [thumbCount, setThumbCount] = useState(null);
  const [thumbFailedCount, setThumbFailedCount] = useState(0);
  const [thumbFailedRows, setThumbFailedRows] = useState([]);
  const [thumbLoading, setThumbLoading] = useState(false);
  const [thumbGenerating, setThumbGenerating] = useState(false);
  const [thumbResults, setThumbResults] = useState(null);

  // --- No Variants (transcode-failure) tab state ---
  const [nvList, setNvList] = useState([]);
  const [nvCount, setNvCount] = useState(0);
  const [nvPage, setNvPage] = useState(1);
  const [nvLoading, setNvLoading] = useState(false);
  const [nvSelected, setNvSelected] = useState([]);
  const [nvBusy, setNvBusy] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('reprocess_inflight', JSON.stringify(inFlightIds));
  }, [inFlightIds]);

  useEffect(() => {
    if (!isAuthenticated || userData?.roleDisplayName !== "Super_admin") {
      navigate("/");
    }
  }, [isAuthenticated, userData, navigate]);

  // --- Processing Queue logic ---
  const fetchList = useCallback(async (page, hours) => {
    setLoading(true);
    const offset = (page - 1) * itemsCountPerPage;
    try {
      const response = await apiCallingTriggerPost("api/listProcessingVideos", {
        offset,
        max: itemsCountPerPage,
        olderThanHours: hours > 0 ? hours : null
      });
      const data = response.data;
      if (data.success === "1") {
        setVideoList(data.result);
        setTotalCount(data.totalCount);
        const returnedIds = data.result.map(v => v.id);
        setInFlightIds(prev => prev.filter(id => returnedIds.includes(id)));
      } else {
        setVideoList([]);
        setTotalCount(0);
        setInFlightIds([]);
      }
    } catch (error) {
      console.error("Error fetching processing videos:", error);
      toast.error(MSG.loadProcessingFailed);
      setVideoList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [itemsCountPerPage]);

  useEffect(() => {
    fetchList(activePage, appliedHours);
  }, [activePage, appliedHours, fetchList]);

  const applyFilter = () => {
    const h = parseFloat(olderThanHours);
    setAppliedHours(isNaN(h) || h < 0 ? 0 : h);
    setActivePage(1);
    setSelectedIds([]);
  };

  const handlePageChange = (page) => {
    setActivePage(page);
    setSelectedIds([]);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const selectableIds = videoList.filter(v => !inFlightIds.includes(v.id)).map(v => v.id);
    if (selectableIds.every(id => selectedIds.includes(id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableIds);
    }
  };

  const doRetry = async (ids) => {
    setRetrying(true);
    setConfirmMode(null);
    setInFlightIds(prev => [...new Set([...prev, ...ids])]);
    try {
      const response = await apiCallingTriggerPost("api/retryProcessingVideos", {
        videoIds: ids,
        initiatedBy: userData?.email || userData?.name || "super_admin"
      });
      const data = response.data;
      if (data.success === "1") {
        toast.success(`${data.queued} video${data.queued !== 1 ? "s" : ""} queued for reprocessing.`);
        setSelectedIds([]);
        setTimeout(() => fetchList(activePage, appliedHours), 1500);
      } else {
        toast.error(MSG.retryFailed);
      }
    } catch (error) {
      console.error("Retry error:", error);
      toast.error(MSG.retrySendFailed);
    } finally {
      setRetrying(false);
    }
  };

  const handleRetrySelected = () => {
    if (selectedIds.length === 0) return;
    setConfirmMode("selected");
  };

  const handleRetryAll = () => {
    if (totalCount === 0) return;
    setConfirmMode("all");
  };

  const confirmRetry = async () => {
    if (confirmMode === "selected") {
      await doRetry(selectedIds);
    } else if (confirmMode === "all") {
      setRetrying(true);
      setConfirmMode(null);
      try {
        const response = await apiCallingTriggerPost("api/listProcessingVideos", {
          offset: 0,
          max: 1000,
          olderThanHours: appliedHours > 0 ? appliedHours : null
        });
        const data = response.data;
        if (data.success === "1" && data.result.length > 0) {
          const allIds = data.result.map(v => v.id);
          await doRetry(allIds);
        } else {
          toast.info(MSG.noVideosToRetry);
          setRetrying(false);
        }
      } catch (error) {
        toast.error(MSG.retryIdsFailed);
        setRetrying(false);
      }
    }
  };

  const selectableOnPage = videoList.filter(v => !inFlightIds.includes(v.id));
  const allOnPageSelected = selectableOnPage.length > 0 && selectableOnPage.every(v => selectedIds.includes(v.id));

  // --- Missing Thumbnails logic ---
  const fetchThumbCount = useCallback(async () => {
    setThumbLoading(true);
    try {
      const response = await apiCallingTriggerPost("api/countMissingThumbnails", {});
      const data = response.data;
      if (data.success === "1") {
        setThumbCount(data.count);
        // BE aliases verified: failedCount, failedRows[{id, name, reason, "failedAt"}]
        setThumbFailedCount(data.failedCount ?? 0);
        setThumbFailedRows(data.failedRows ?? []);
      } else {
        toast.error(MSG.loadThumbCountFailed);
      }
    } catch (error) {
      console.error("Error fetching thumbnail count:", error);
      toast.error(MSG.loadThumbCountFailed);
    } finally {
      setThumbLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'thumbnails' && thumbCount === null) {
      fetchThumbCount();
    }
  }, [activeTab, thumbCount, fetchThumbCount]);

  const handleGenerateThumbnails = async (retryFailed = false) => {
    setThumbGenerating(true);
    setThumbResults(null);
    try {
      const response = await apiCallingTriggerPost("api/generateMissingThumbnails", { retryFailed });
      const data = response.data;
      if (data.success === "1") {
        setThumbResults(data);
        if (data.queued != null) {
          // Fire-and-forget — processing continues in background, refresh count manually
          setThumbCount(null);
          toast.success(`Started — ${data.queued} thumbnails generating in background.`);
        } else {
          setThumbCount(data.summary?.failed ?? 0);
          toast.success(`Done — ${data.summary?.successful || 0} thumbnails generated.`);
        }
      } else {
        toast.error(data.message || data.errorMessage || "Thumbnail generation failed.");
      }
    } catch (error) {
      console.error("Thumbnail generation error:", error);
      toast.error(MSG.thumbGenFailed);
    } finally {
      setThumbGenerating(false);
    }
  };

  // --- No Variants (transcode-failure) logic ---
  // Processed videos with zero playable variants: rows published under the
  // pre-2026-10-15 order (status set at tagging, transcode failed later) and failed
  // RE-transcodes. First-ingest transcode failures now stay 'Processing' and are
  // handled by the stage-aware Retry in the Processing Queue. Repair = reTranscodeVariants with batchSize:1 — it runs one
  // transcode at a time (awaitCompletion) and self-chains the rest in the BACKGROUND,
  // so it never holds more than 1 of the 20 transcoder job slots.
  const fetchNoVariants = useCallback(async (page) => {
    setNvLoading(true);
    const offset = (page - 1) * itemsCountPerPage;
    try {
      const response = await apiCallingTriggerPost("api/listNoVariantVideos", { offset, max: itemsCountPerPage });
      const data = response.data;
      if (data.success === "1") {
        setNvList(data.result);
        setNvCount(data.totalCount);
      } else {
        setNvList([]); setNvCount(0);
      }
    } catch (error) {
      console.error("No-variant list error:", error);
      toast.error("Couldn’t load videos missing variants.");
    } finally {
      setNvLoading(false);
    }
  }, [itemsCountPerPage]);

  useEffect(() => {
    if (activeTab === 'novariants') fetchNoVariants(nvPage);
  }, [activeTab, nvPage, fetchNoVariants]);

  // Populate the tab badge count on mount (cheap COUNT) so the operator sees the
  // backlog without opening the tab; the list refreshes it when the tab is opened.
  const fetchNvCount = useCallback(async () => {
    try {
      const response = await apiCallingTriggerPost("api/countNoVariantVideos", {});
      if (response.data?.success === "1") setNvCount(response.data.count);
    } catch { /* badge count is best-effort */ }
  }, []);

  useEffect(() => { fetchNvCount(); }, [fetchNvCount]);

  const nvSelectableIds = nvList.map(v => v.id);
  const nvAllSelected = nvSelectableIds.length > 0 && nvSelectableIds.every(id => nvSelected.includes(id));
  const nvToggleSelect = (id) =>
    setNvSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const nvToggleSelectAll = () => setNvSelected(nvAllSelected ? [] : nvSelectableIds);
  const handleNvPageChange = (p) => { setNvPage(p); setNvSelected([]); };

  const doRetranscode = async (ids) => {
    if (ids.length === 0 || nvBusy) return;
    setNvBusy(true);
    try {
      const response = await apiCallingTriggerPost("api/reTranscodeVariants", { videoIds: ids, batchSize: 1 });
      const data = response.data;
      if (data.success === "1") {
        const done = data.results?.successful?.length ?? 1;
        const remaining = data.remainingCount ?? Math.max(0, ids.length - 1);
        toast.success(
          remaining > 0
            ? `Re-transcoded ${done}; ${remaining} more continuing in the background (one at a time).`
            : `Re-transcoded ${done} video${done !== 1 ? "s" : ""}.`
        );
        setNvSelected([]);
        setTimeout(() => fetchNoVariants(nvPage), 1500);
      } else {
        toast.error(data.errorMessage || "Re-transcode failed.");
      }
    } catch (error) {
      console.error("Re-transcode error:", error);
      toast.error("Re-transcode request failed.");
    } finally {
      setNvBusy(false);
    }
  };

  return (
    <div className="main-wrapper main-wrapper-cms">
      <AdminHeader title="Reprocess Queue" />

      <div className="container mt-4">

        {/* Tabs */}
        <div className="d-flex mb-4" style={{ borderBottom: '2px solid #dee2e6', gap: '4px' }}>
          <button
            className={activeTab === 'processing' ? 'accept-all-btn' : 'accept-all-btn accept-all-gray'}
            style={{ borderRadius: '4px 4px 0 0', marginBottom: '-2px', borderBottom: activeTab === 'processing' ? '2px solid #fff' : undefined }}
            onClick={() => setActiveTab('processing')}
          >
            Processing Queue {totalCount > 0 && `(${totalCount})`}
          </button>
          <button
            className={activeTab === 'thumbnails' ? 'accept-all-btn' : 'accept-all-btn accept-all-gray'}
            style={{ borderRadius: '4px 4px 0 0', marginBottom: '-2px', borderBottom: activeTab === 'thumbnails' ? '2px solid #fff' : undefined }}
            onClick={() => setActiveTab('thumbnails')}
          >
            Missing Thumbnails {thumbCount > 0 && `(${thumbCount})`}
          </button>
          <button
            className={activeTab === 'novariants' ? 'accept-all-btn' : 'accept-all-btn accept-all-gray'}
            style={{ borderRadius: '4px 4px 0 0', marginBottom: '-2px', borderBottom: activeTab === 'novariants' ? '2px solid #fff' : undefined }}
            onClick={() => setActiveTab('novariants')}
          >
            No Variants {nvCount > 0 && `(${nvCount})`}
          </button>
        </div>

        {/* ── Processing Queue Tab ── */}
        {activeTab === 'processing' && (
          <>
            {/* Filter + action row */}
            <div className="d-flex align-items-center flex-wrap justify-content-between mb-4">
              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                <label className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                  Older than (hours):
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={olderThanHours}
                  onChange={e => setOlderThanHours(e.target.value)}
                  className="form-control mb-0"
                  style={{ width: 90 }}
                  onKeyDown={e => e.key === 'Enter' && applyFilter()}
                />
                <button className="accept-all-btn" onClick={applyFilter} disabled={loading}>
                  Apply
                </button>
                <button
                  className="accept-all-btn accept-all-gray"
                  onClick={() => { setOlderThanHours(0); setAppliedHours(0); setActivePage(1); setSelectedIds([]); }}
                  disabled={loading}
                >
                  Show all
                </button>
                <span className="ms-2">
                  Total stuck: <strong>{totalCount}</strong>
                </span>
              </div>

              <div className="d-flex" style={{ gap: '8px' }}>
                <button
                  className="accept-all-btn"
                  onClick={handleRetrySelected}
                  disabled={selectedIds.length === 0 || retrying}
                >
                  Retry Selected ({selectedIds.length})
                </button>
                <button
                  className="accept-all-btn"
                  onClick={handleRetryAll}
                  disabled={totalCount === 0 || retrying}
                  style={{ background: '#dc3545', borderColor: '#dc3545' }}
                >
                  Retry All ({totalCount})
                </button>
                <button
                  className="accept-all-btn accept-all-gray"
                  onClick={() => fetchList(activePage, appliedHours)}
                  disabled={loading}
                >
                  Refresh
                </button>
              </div>
            </div>

            {/* Confirmation modal */}
            {confirmMode && (
              <div className="modal-admin-wrapper multiple-upload">
                <div className="modal-ad-popup modal-md">
                  <div className="modal-innerpopup">
                    <div className="modal-headdiv d-flex justify-content-between">
                      <h5 className="modal-head w-100 text-center">
                        {confirmMode === "selected"
                          ? `Retry ${selectedIds.length} selected video${selectedIds.length !== 1 ? "s" : ""}?`
                          : `Retry ALL ${totalCount} stuck videos?`
                        }
                      </h5>
                    </div>
                    <p className="text-center" style={{ padding: '0 20px', color: '#666' }}>
                      This re-runs the next pipeline stage for each video: renditions first if they are missing, then AI tagging from the rendition.
                    </p>
                  </div>
                  <div className="tag-popup-footer">
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                      <button
                        type="button"
                        className="accept-all-btn w-100 py-2 accept-all-gray"
                        onClick={() => setConfirmMode(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="accept-all-btn w-100 py-2 d-flex align-items-center justify-content-center"
                        onClick={confirmRetry}
                        disabled={retrying}
                        style={{ gap: '6px' }}
                      >
                        {retrying ? "Queuing..." : "Confirm"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="table-responsive">
              {loading ? (
                <div className="noData d-flex flex-column">
                  <div>
                    <img src={Loader} alt="loading" className="img-fluid" width="150" height="150" />
                  </div>
                </div>
              ) : videoList.length === 0 ? (
                <div className="noData d-flex flex-column">
                  <div>
                    <img src={noData} alt="no data" className="img-fluid" width="150" height="150" />
                  </div>
                  <div>No stuck Processing videos found.</div>
                </div>
              ) : (
                <>
                  <table className="table table-bordered admin-list-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>
                          <input
                            type="checkbox"
                            checked={allOnPageSelected}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th>ID</th>
                        <th>Name</th>
                        <th className="text-center" style={{ whiteSpace: 'nowrap' }}>Created</th>
                        <th className="text-center" style={{ whiteSpace: 'nowrap' }}>Video Date</th>
                        <th className="text-center">Status</th>
                        <th>Recorded failure</th>
                        <th className="text-center" style={{ width: 90 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {videoList.map(video => {
                        const isInFlight = inFlightIds.includes(video.id);
                        return (
                          <tr key={video.id} style={isInFlight ? { background: '#e8f5e9', opacity: 0.8 } : selectedIds.includes(video.id) ? { background: '#fff8e1' } : {}}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(video.id)}
                                onChange={() => toggleSelect(video.id)}
                                disabled={isInFlight}
                              />
                            </td>
                            <td>{video.id}</td>
                            <td style={{ maxWidth: 300, wordBreak: 'break-word' }}>
                              {video.name || <span className="sub-text">—</span>}
                            </td>
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              {video.created ? moment(video.created).format("DD MMM YYYY HH:mm") : "—"}
                            </td>
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              {video.video_date ? moment(video.video_date).format("DD MMM YYYY") : <span className="sub-text">—</span>}
                            </td>
                            <td className="text-center">
                              {isInFlight
                                ? <span style={{ fontSize: 12, padding: '2px 8px', color: '#2e7d32', fontWeight: 600 }}>Queued</span>
                                : <span className="open" style={{ fontSize: 12, padding: '2px 8px' }}>{video.status}</span>
                              }
                            </td>
                            <td style={{ maxWidth: 340, overflowWrap: 'anywhere', fontSize: 13 }}>
                              {/* BE aliases verified: "failedStage" / "failureReason" (listProcessingVideos) */}
                              {video.failedStage
                                ? <span style={{ color: '#c62828' }}><strong>{video.failedStage}</strong>{video.failureReason ? ` — ${video.failureReason}` : ''}</span>
                                : <span className="sub-text">—</span>}
                            </td>
                            <td className="text-center">
                              <button
                                className="accept-all-btn"
                                disabled={retrying || isInFlight}
                                style={{ padding: '4px 12px', fontSize: 12 }}
                                onClick={() => {
                                  setSelectedIds([video.id]);
                                  setConfirmMode("selected");
                                }}
                              >
                                Retry
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {totalCount > itemsCountPerPage && (
                    <div className="d-flex align-items-center flex-wrap justify-content-end my-4 page-division">
                      <div className="ms-auto">
                        <div className="pagination-wrapper m-0 p-0">
                          <Pagination
                            activePage={activePage}
                            itemsCountPerPage={itemsCountPerPage}
                            totalItemsCount={totalCount}
                            pageRangeDisplayed={5}
                            onChange={handlePageChange}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* ── Missing Thumbnails Tab ── */}
        {activeTab === 'thumbnails' && (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap" style={{ gap: '12px' }}>
              <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                {thumbLoading ? (
                  <span className="sub-text">Loading count...</span>
                ) : (
                  <span>
                    Videos missing thumbnails: <strong>{thumbCount ?? '—'}</strong>
                    {thumbFailedCount > 0 && (
                      <span style={{ color: '#c62828', marginLeft: 8 }}>
                        • previously failed: <strong>{thumbFailedCount}</strong>
                      </span>
                    )}
                  </span>
                )}
                <button
                  className="accept-all-btn accept-all-gray"
                  onClick={() => { setThumbCount(null); setThumbResults(null); }}
                  disabled={thumbLoading || thumbGenerating}
                >
                  Refresh
                </button>
              </div>
              <div className="d-flex" style={{ gap: '12px' }}>
                <button
                  className="accept-all-btn"
                  onClick={() => handleGenerateThumbnails(false)}
                  disabled={thumbGenerating || thumbLoading || thumbCount === 0}
                  title="Skips files with a recorded failure — they fail identically until their source is fixed"
                >
                  {thumbGenerating ? "Generating..." : "Generate All Missing Thumbnails"}
                </button>
                {thumbFailedCount > 0 && (
                  <button
                    className="accept-all-btn accept-all-gray"
                    onClick={() => handleGenerateThumbnails(true)}
                    disabled={thumbGenerating || thumbLoading}
                    title="Re-attempts the previously-failed files too (re-downloads each one — can be slow for large videos)"
                  >
                    Retry Failed Too ({thumbFailedCount})
                  </button>
                )}
              </div>
            </div>

            {!thumbGenerating && thumbFailedRows.length > 0 && (
              <div className="mb-4">
                <div className="mb-2" style={{ fontWeight: 600, color: '#c62828' }}>
                  Previously failed — excluded from Generate ({thumbFailedCount})
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered admin-list-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Recorded reason</th>
                        <th>Failed at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {thumbFailedRows.map(f => (
                        <tr key={f.id}>
                          <td>{f.id}</td>
                          <td>{f.name}</td>
                          <td style={{ maxWidth: 520, overflowWrap: 'anywhere', fontSize: 13 }}>{f.reason || '—'}</td>
                          <td>{f.failedAt && !isNaN(Date.parse(f.failedAt)) ? new Date(f.failedAt).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {thumbGenerating && (
              <div className="noData d-flex flex-column">
                <div>
                  <img src={Loader} alt="loading" className="img-fluid" width="150" height="150" />
                </div>
                <div style={{ color: '#666', marginTop: 8 }}>
                  Generating thumbnails — this may take a few minutes...
                </div>
              </div>
            )}

            {!thumbGenerating && thumbResults && (
              <div>
                <div className="d-flex flex-wrap mb-4" style={{ gap: '16px' }}>
                  <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '12px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#2e7d32' }}>{thumbResults.summary?.successful ?? 0}</div>
                    <div style={{ fontSize: 13, color: '#555' }}>Successful</div>
                  </div>
                  <div style={{ background: thumbResults.summary?.failed > 0 ? '#ffebee' : '#f5f5f5', border: `1px solid ${thumbResults.summary?.failed > 0 ? '#ef9a9a' : '#e0e0e0'}`, borderRadius: 6, padding: '12px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: thumbResults.summary?.failed > 0 ? '#c62828' : '#757575' }}>{thumbResults.summary?.failed ?? 0}</div>
                    <div style={{ fontSize: 13, color: '#555' }}>Failed</div>
                  </div>
                  <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 6, padding: '12px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#1565c0' }}>{thumbResults.summary?.totalProcessed ?? 0}</div>
                    <div style={{ fontSize: 13, color: '#555' }}>Total</div>
                  </div>
                </div>

                {thumbResults.failed?.length > 0 && (
                  <div>
                    <div className="mb-2" style={{ fontWeight: 600, color: '#c62828' }}>
                      Failed ({thumbResults.failed.length})
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered admin-list-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Reason</th>
                            <th>URL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {thumbResults.failed.map(f => (
                            <tr key={f.id}>
                              <td>{f.id}</td>
                              <td style={{ color: '#c62828' }}>{f.reason}</td>
                              <td style={{ maxWidth: 300, wordBreak: 'break-all', fontSize: 12 }}>{f.originalUrl}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!thumbGenerating && !thumbResults && thumbCount === 0 && thumbFailedCount === 0 && (
              <div className="noData d-flex flex-column">
                <div>
                  <img src={noData} alt="no data" className="img-fluid" width="150" height="150" />
                </div>
                <div>No missing thumbnails found.</div>
              </div>
            )}
          </div>
        )}

        {/* ── No Variants Tab ── */}
        {activeTab === 'novariants' && (
          <>
            <div className="mb-3" style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '10px 16px', fontSize: 13, color: '#6d4c41' }}>
              <strong>Processed</strong> videos with <strong>no playable variants</strong> — a failed re-transcode, or a row published before renditions-first processing (Oct 2026).
              Re-transcoding runs <strong>one video at a time</strong> (holding 1 of the 20 transcoder slots); the first is done now and a larger
              selection <strong>continues in the background</strong> and can take a while. Avoid running this during bulk uploads or the re-transcode job.
            </div>

            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: '12px' }}>
              <span>Processed videos with no variants: <strong>{nvCount}</strong></span>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <button
                  className="accept-all-btn accept-all-gray"
                  onClick={() => fetchNoVariants(nvPage)}
                  disabled={nvLoading || nvBusy}
                >
                  Refresh
                </button>
                <button
                  className="accept-all-btn"
                  onClick={() => doRetranscode(nvSelected)}
                  disabled={nvBusy || nvLoading || nvSelected.length === 0}
                >
                  {nvBusy ? "Re-transcoding..." : `Re-transcode selected${nvSelected.length ? ` (${nvSelected.length})` : ""}`}
                </button>
              </div>
            </div>

            <div className="table-responsive">
              {nvLoading ? (
                <div className="noData d-flex flex-column">
                  <div><img src={Loader} alt="loading" className="img-fluid" width="150" height="150" /></div>
                </div>
              ) : nvList.length === 0 ? (
                <div className="noData d-flex flex-column">
                  <div><img src={noData} alt="no data" className="img-fluid" width="150" height="150" /></div>
                  <div>No Processed videos are missing variants.</div>
                </div>
              ) : (
                <>
                  <table className="table table-bordered admin-list-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>
                          <input type="checkbox" checked={nvAllSelected} onChange={nvToggleSelectAll} />
                        </th>
                        <th>ID</th>
                        <th>Name</th>
                        <th className="text-center" style={{ whiteSpace: 'nowrap' }}>Created</th>
                        <th className="text-center" style={{ whiteSpace: 'nowrap' }}>Video Date</th>
                        <th className="text-center" style={{ width: 120 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nvList.map(video => (
                        <tr key={video.id} style={nvSelected.includes(video.id) ? { background: '#fff8e1' } : {}}>
                          <td>
                            <input type="checkbox" checked={nvSelected.includes(video.id)} onChange={() => nvToggleSelect(video.id)} />
                          </td>
                          <td>{video.id}</td>
                          <td style={{ maxWidth: 300, wordBreak: 'break-word' }}>
                            {video.name || <span className="sub-text">—</span>}
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            {video.created ? moment(video.created).format("DD MMM YYYY HH:mm") : "—"}
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            {video.video_date ? moment(video.video_date).format("DD MMM YYYY") : <span className="sub-text">—</span>}
                          </td>
                          <td className="text-center">
                            <button
                              className="accept-all-btn"
                              disabled={nvBusy}
                              style={{ padding: '4px 12px', fontSize: 12 }}
                              onClick={() => doRetranscode([video.id])}
                            >
                              Re-transcode
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {nvCount > itemsCountPerPage && (
                    <div className="d-flex align-items-center flex-wrap justify-content-end my-4 page-division">
                      <div className="ms-auto">
                        <div className="pagination-wrapper m-0 p-0">
                          <Pagination
                            activePage={nvPage}
                            itemsCountPerPage={itemsCountPerPage}
                            totalItemsCount={nvCount}
                            pageRangeDisplayed={5}
                            onChange={handleNvPageChange}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export default Reprocess;
